import { useEffect, useState } from 'react';
import { fetchSiteBranding, type SiteConsultationConfig } from './api';
import OneliveNativeConsultWidget from './OneliveNativeConsultWidget';

declare global {
  interface Window {
    ChannelIO?: (...args: unknown[]) => void;
    ChannelIOInitialized?: boolean;
  }
}

function getCurrentMemberLevel(): number {
  try {
    const state = (window as any).G7Core?.state?.get?.() || {};
    const user = state._global?.currentUser ?? state.currentUser;
    const level = Number(user?.member_level);
    return Number.isFinite(level) && level > 0 ? level : 1;
  } catch {
    return 1;
  }
}

function canUseConsultation(config: SiteConsultationConfig): boolean {
  const minLevel = Number(config.min_member_level) || 1;
  return getCurrentMemberLevel() >= minLevel;
}

function loadChannelIoScript(): void {
  if (window.ChannelIO) {
    return;
  }

  const ch = function (...args: unknown[]) {
    (ch as any).c(args);
  } as Window['ChannelIO'] & { q: unknown[][]; c: (args: unknown[]) => void };

  ch.q = [];
  ch.c = (args) => {
    ch.q.push(args);
  };
  window.ChannelIO = ch;

  const load = () => {
    if (window.ChannelIOInitialized) {
      return;
    }
    window.ChannelIOInitialized = true;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://cdn.channel.io/plugin/ch-plugin-web.js';
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
  };

  if (document.readyState === 'complete') {
    load();
  } else {
    window.addEventListener('DOMContentLoaded', load);
    window.addEventListener('load', load);
  }
}

function bootChannelIo(pluginKey: string): void {
  loadChannelIoScript();

  try {
    const state = (window as any).G7Core?.state?.get?.() || {};
    const user = state._global?.currentUser ?? state.currentUser;
    const bootConfig: Record<string, unknown> = {
      pluginKey,
      language: 'ko',
    };

    if (user) {
      const memberId = String(user.username || user.uuid || '').trim();
      if (memberId) {
        bootConfig.memberId = memberId;
      }
      bootConfig.profile = {
        name: String(user.nickname || user.name || memberId || '').trim(),
        email: String(user.email || '').trim(),
        mobileNumber: String(user.phone || user.mobile || '').trim(),
      };
    }

    window.ChannelIO?.('boot', bootConfig);
  } catch {
    window.ChannelIO?.('boot', { pluginKey, language: 'ko' });
  }
}

function injectTidioScript(scriptHtml: string): void {
  const markerId = 'onelive-tidio-consult-script';
  if (document.getElementById(markerId)) {
    return;
  }

  const container = document.createElement('div');
  container.id = markerId;
  container.innerHTML = scriptHtml;
  document.body.appendChild(container);

  container.querySelectorAll('script').forEach((oldScript) => {
    const script = document.createElement('script');
    Array.from(oldScript.attributes).forEach((attr) => {
      script.setAttribute(attr.name, attr.value);
    });
    script.text = oldScript.textContent || '';
    oldScript.replaceWith(script);
  });
}

function activateThirdPartyConsultation(config: SiteConsultationConfig): void {
  if (!canUseConsultation(config)) {
    return;
  }

  const pluginKey = (config.channel_plugin_key || '').trim();
  if (pluginKey) {
    bootChannelIo(pluginKey);
    return;
  }

  const tidioScript = (config.tidio_script || '').trim();
  if (tidioScript) {
    injectTidioScript(tidioScript);
  }
}

export default function OneliveConsultWidget() {
  const [consultation, setConsultation] = useState<SiteConsultationConfig | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetchSiteBranding()
      .then((data) => {
        if (cancelled || !data?.consultation) {
          return;
        }
        setConsultation(data.consultation);
        if (!data.consultation.native_enabled) {
          activateThirdPartyConsultation(data.consultation);
        }
      })
      .catch(() => {
        // optional widget
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!consultation?.native_enabled) {
    return null;
  }

  return (
    <OneliveNativeConsultWidget
      enabled={consultation.native_enabled}
      agentName={consultation.agent_name || '상담원'}
      agentImage={consultation.agent_image || '/img/no_profile.svg'}
      ringDurationSec={consultation.ring_duration_sec || 8}
    />
  );
}
